<script setup lang="ts">
import { formatCurrency } from "@/composables/format";
import { isGuest, getSessionUserInfo } from "@/composables/session";
import { hasFeature, usePortalSettings, useWebsiteSettings } from "@/composables/settings";
import { useCart } from "@/composables/useCart";
import { useCommitmentsLabel, useCommitmentsSummary } from "@/composables/useCommitments";
import { hasPermission } from "@/composables/usePermissions";
import { BBadge, BButton, BSidebarLink } from "@/dokos-ui";
import { computed } from "vue";

const ws = useWebsiteSettings();
const bps = usePortalSettings();
const cart = useCart();
const commitments = useCommitmentsSummary();
const commitmentsLabel = useCommitmentsLabel();
const userInfo = getSessionUserInfo();

const custom_links = computed(() => {
  return (bps.value?.links || []).filter(l => !l.limit_to_role || userInfo.data?.roles.includes(l.limit_to_role))
})

const normalizeUrl = (url: string) => {
  try {
    return new URL(url, window.location.origin).href
  } catch (e) {
    return url
  }
}
</script>

<template>
  <BSidebarLink :to="{ name: 'Home' }" icon="home" label="Accueil" />
  <BSidebarLink :to="{ name: 'Resources' }" icon="clock" label="Réserver" v-if="hasFeature('bookings')" />
  <BSidebarLink :to="{ name: 'Events' }" icon="calendar" label="Évènements" v-if="hasFeature('events')" />
  <BSidebarLink
    v-if="!isGuest() && hasFeature('credits')"
    :to="{ name: 'CreditsHome' }"
    icon="plus-circle"
    label="Crédits"
  />

  <hr />

  <!-- <BSidebarLink href="/forum" icon="message-circle" label="Forum" /> -->
  <BSidebarLink :to="{ name: 'CreationGuides' }" icon="book-open" label="Guides de création" v-if="bps?.enable_creation_guide" />
  <BSidebarLink :to="{ name: 'Blog Posts' }" icon="bell" :label="bps?.blog_title || 'Actualités'" v-if="hasFeature('blog') && bps?.blog_enabled" />
  <BSidebarLink :to="{ name: 'Articles' }" icon="coffee" label="Base de connaissances" v-if="hasFeature('knowledge-base') && bps?.kb_enabled"/>
  <BSidebarLink
    v-if="bps?.pubprofiles_enabled"
    :to="{ name: 'Published Profiles' }"
    icon="users"
    label="Trombinoscope"
  />

  <BSidebarLink :to="{ name: 'Tickets' }" icon="help-circle" label="Tickets" v-if="!isGuest() && bps?.tickets_enabled"/>
  <BSidebarLink v-if="!isGuest() && bps?.event_slots_enabled" :to="{ name: 'Event Slots' }" icon="trello" label="Postes d'évènement" />

  <BSidebarLink
    v-for="link in custom_links"
    as="a"
    :href="link.url"
    :icon="link.icon"
    :label="link.label"
    :target="link.open_in_a_new_tab ? '_blank' : '_self'"
  />

  <hr />

  <BSidebarLink v-if="isGuest()" :to="{ name: 'Login' }" icon="user" label="Se connecter" />
  <template v-else>
    <BSidebarLink to="/account" icon="user" label="Mon compte" />
    <BSidebarLink
      v-if="cart?.items?.length"
      to="/cart"
      icon="shopping-cart"
      label="Panier"
    >
      <div class="relative">
        <span> Panier </span>
        <BBadge v-if="cart?.total_qty" size="sm" theme="red" class="font-bold tabular-nums">
          {{ cart.total_qty }}
        </BBadge>
      </div>
      <span v-if="cart?.total_qty" class="flex-1 text-right text-sm truncate">
        {{ formatCurrency(cart.grand_total, cart.currency) }}
        TTC
      </span>
    </BSidebarLink>
    <BSidebarLink to="/orders" icon="package" label="Commandes" v-if="!isGuest() && bps?.orders_enabled"/>
    <BSidebarLink to="/invoices" icon="file-text" label="Factures" v-if="!isGuest() && bps?.invoices_enabled"/>
    <BSidebarLink to="/contracts" icon="pen-tool" :label="commitmentsLabel" v-if="bps?.contracts_enabled || commitments.data?.subscriptions"/>
    <BSidebarLink :to="{ name: 'Documents' }" icon="file" label="Documents" v-if="bps?.documents_enabled"/>
    <BSidebarLink to="/members" icon="users" label="Organisation" v-if="hasPermission('p_manage_members') && bps?.perm_management_enabled" />
  </template>

  <hr />
  <BSidebarLink href="/" target="" icon="globe" label="Retourner au site web" />

  <div class="flex-1 grow" />
  <hr />

  <BButton
    v-for="it in ws?.footer_items"
    size="xs"
    variant="text"
    :href="normalizeUrl(it.url)"
  >
    {{ it.label }}
  </BButton>
  <div class="text-xs" v-if="ws?.copyright">
    {{ ws?.copyright }}
  </div>
  <!-- <div class="text-2xs" v-if="ws?.address" v-html="ws?.address" /> -->
  <!-- <hr class="sticky bottom-0" /> -->
</template>
